import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useSelector } from "react-redux";

import { Avatar, DesignToken, RadiusToken } from "@livechat/design-system-react-components";

import { Dropdown, DropdownDirection, DropdownPlacement, DropdownShadow } from "components/dropdown";
import { newFeaturesCampaigns } from "constants/campaigns";
import { useChatWidget } from "hooks/use-chat-widget";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { NavigationItems } from "interfaces/navigationItem";
import { IAgent } from "store/entities/agents";
import { selectFeatureToggleProductSwitcher } from "store/entities/license";
import { selectTheme } from "store/features/app";
import { withVar } from "themes/helpers";

import NavigationMyProfileMenu from "./NavigationMyProfileMenu";
import { NavigationMyProfileTooltip } from "./NavigationMyProfileTooltip";

interface IProps {
  agent: IAgent;
  onLogout(): void;
  toggleShortcutsModalDisplayed(): void;
}

export const NavigationMyProfile: FC<IProps> = ({ agent, onLogout, toggleShortcutsModalDisplayed }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const { openChatWidget } = useChatWidget();
  const {
    isNewForAgent: isAgentCreatedBeforeAgentAutoAssignmentManagementRelease,
    featureFlagValue: hasSeenAutoAssignmentSwitcherInProfileMenu,
    setAgentFlagCallback: markAssignmentSwitcherInProfileMenuAsSeen,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenAutoAssignmentSwitcherInProfileMenu.flag,
    newFeaturesCampaigns.hasSeenAutoAssignmentSwitcherInProfileMenu.campaignStartDate
  );
  const shouldDisplayAssignmentSwitcherNewBadge =
    isAgentCreatedBeforeAgentAutoAssignmentManagementRelease && !hasSeenAutoAssignmentSwitcherInProfileMenu;

  const theme = useSelector(selectTheme);
  const productSwitcherEnabled = useSelector(selectFeatureToggleProductSwitcher);

  const handleAvatarClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();

    setIsMenuVisible(prevState => !prevState);
  };

  const handleHideMenu = (): void => {
    if (shouldDisplayAssignmentSwitcherNewBadge) markAssignmentSwitcherInProfileMenuAsSeen();
    setIsMenuVisible(false);
  };

  return (
    <Dropdown
      content={
        <NavigationMyProfileMenu
          agent={agent}
          productSwitcherEnabled={productSwitcherEnabled}
          currentTheme={theme}
          onHideMenu={handleHideMenu}
          onLogout={onLogout}
          openChatWidget={openChatWidget}
          toggleShortcutsModalDisplayed={toggleShortcutsModalDisplayed}
        />
      }
      directions={[DropdownDirection.Right]}
      placement={DropdownPlacement.Bottom}
      offset={-5}
      isVisible={isMenuVisible}
      onHideDropdown={handleHideMenu}
      shadow={DropdownShadow.Strong}
    >
      <NavigationMyProfileTooltip
        triggerRenderer={
          <StyledNavigationMyProfile
            onClick={handleAvatarClick}
            data-testid={`nav-${NavigationItems.MyProfile}`}
            menuOpened={isMenuVisible}
          >
            <Avatar
              type={agent.avatar ? "image" : "text"}
              src={agent.avatar ? agent.avatar : undefined}
              text={agent.name ?? agent.email}
              size="small"
            />
          </StyledNavigationMyProfile>
        }
      />
    </Dropdown>
  );
};

const activeBackground = css`
  background: color-mix(in srgb, ${withVar(DesignToken.SurfaceLockedWhite)} 15%, transparent);
`;

const StyledNavigationMyProfile = styled.a<{ menuOpened: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 42px;
  margin: 0 6px;
  border-radius: ${withVar(RadiusToken.Radius3)};
  cursor: pointer;
  ${({ menuOpened }): SerializedStyles | string => (menuOpened ? activeBackground : "")};

  &:hover {
    ${activeBackground};
  }
`;
